/**
 * Faithful port of WritingGeometryEvaluator.swift.
 * Pure polyline geometry. No OCR / ML.
 */

import { drawingTemplateForCharacter } from "./writingReferenceTemplates";

export const WRITING_DIFFICULTY = {
  easy: {
    acceptanceThreshold: 0.66,
    toleranceFraction: 0.16,
    minimumCoverage: 0.62,
    minimumPrecision: 0.56,
    acceptedInkLengthRange: [0.55, 2.4],
  },
  medium: {
    acceptanceThreshold: 0.76,
    toleranceFraction: 0.115,
    minimumCoverage: 0.72,
    minimumPrecision: 0.66,
    acceptedInkLengthRange: [0.65, 2.0],
  },
  hard: {
    acceptanceThreshold: 0.85,
    toleranceFraction: 0.078,
    minimumCoverage: 0.82,
    minimumPrecision: 0.76,
    acceptedInkLengthRange: [0.75, 1.7],
  },
  /**
   * Writing from memory (no guide): the ink is slid + scaled onto the
   * template first, so "easy" tolerance would wave through random
   * scribbles (43% in simulation). Medium tolerance with easy gates keeps
   * 97–100% of wobbly, off-centre, 70%-size child letters and drops
   * scribble false-positives to ~15%.
   */
  freeform: {
    acceptanceThreshold: 0.66,
    toleranceFraction: 0.115,
    minimumCoverage: 0.62,
    minimumPrecision: 0.56,
    acceptedInkLengthRange: [0.55, 2.4],
  },
};

const hypot = (dx, dy) => Math.hypot(dx, dy);

function densify(points, isClosed, spacing) {
  if (!points?.length) return [];
  if (points.length === 1) return [points[0]];
  const result = [points[0]];
  const pairs = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    pairs.push([points[i], points[i + 1]]);
  }
  const first = points[0];
  const last = points[points.length - 1];
  if (isClosed && (last.x !== first.x || last.y !== first.y)) {
    pairs.push([last, first]);
  }
  for (const [start, end] of pairs) {
    const distance = hypot(end.x - start.x, end.y - start.y);
    const steps = Math.max(1, Math.ceil(distance / Math.max(1, spacing)));
    for (let step = 1; step <= steps; step += 1) {
      const progress = step / steps;
      result.push({
        x: start.x + (end.x - start.x) * progress,
        y: start.y + (end.y - start.y) * progress,
      });
    }
  }
  return result;
}

function polylineLength(points, isClosed) {
  if (!points?.length) return 0;
  let length = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    length += hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
  }
  if (isClosed) {
    const first = points[0];
    const last = points[points.length - 1];
    if (last.x !== first.x || last.y !== first.y) {
      length += hypot(first.x - last.x, first.y - last.y);
    }
  }
  return length;
}

function boundsOf(points) {
  if (!points.length) return null;
  let minX = points[0].x;
  let maxX = points[0].x;
  let minY = points[0].y;
  let maxY = points[0].y;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY, midX: (minX + maxX) / 2, midY: (minY + maxY) / 2 };
}

function similarity(source, destination, tolerance) {
  if (!source.length || !destination.length) return 0;
  let sum = 0;
  for (const point of source) {
    let nearestSquared = Number.POSITIVE_INFINITY;
    for (const candidate of destination) {
      const dx = point.x - candidate.x;
      const dy = point.y - candidate.y;
      nearestSquared = Math.min(nearestSquared, dx * dx + dy * dy);
    }
    const normalizedSquared = nearestSquared / Math.max(1, tolerance * tolerance);
    sum += Math.exp(-0.5 * normalizedSquared);
  }
  return sum / source.length;
}

function inkLengthPenalty(ratio, acceptedRange) {
  const [low, high] = acceptedRange;
  if (ratio >= low && ratio <= high) return 1;
  if (ratio < low) {
    const shortfall = (low - ratio) / 0.28;
    return Math.exp(-(shortfall * shortfall));
  }
  const excess = (ratio - high) / 0.72;
  return Math.exp(-(excess * excess));
}

function assign(points, glyphs) {
  const result = glyphs.map(() => []);
  for (const point of points) {
    let nearest = 0;
    let best = Number.POSITIVE_INFINITY;
    glyphs.forEach((glyph, index) => {
      const dist = Math.abs(point.x - glyph.frame.midX);
      if (dist < best) {
        best = dist;
        nearest = index;
      }
    });
    result[nearest].push(point);
  }
  return result;
}

function assignLengths(strokes, glyphs) {
  const result = glyphs.map(() => 0);
  for (const stroke of strokes) {
    for (let i = 0; i < stroke.points.length - 1; i += 1) {
      const start = stroke.points[i];
      const end = stroke.points[i + 1];
      const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
      let nearest = 0;
      let best = Number.POSITIVE_INFINITY;
      glyphs.forEach((glyph, index) => {
        const dist = Math.abs(midpoint.x - glyph.frame.midX);
        if (dist < best) {
          best = dist;
          nearest = index;
        }
      });
      result[nearest] += hypot(end.x - start.x, end.y - start.y);
    }
  }
  return result;
}

/**
 * Freeform placement: a child writing "from memory" on a big pad draws the
 * letter wherever and however large they like. Slide + uniformly scale the
 * ink so its bounds sit on the template bounds before scoring. The scale
 * follows the template's dominant axis (height for almost every glyph) so a
 * thin "l" or a dotted "i" is not shrunk to its narrow width.
 */
function fitToTarget(ink, layout) {
  const inkPoints = ink.flatMap((s) => s.points);
  const targetPoints = layout.glyphs.flatMap((g) => g.variants[0].strokes.flatMap((s) => s.points));
  const inkBounds = boundsOf(inkPoints);
  const targetBounds = boundsOf(targetPoints);
  if (!inkBounds || !targetBounds) return ink;
  const heightRatio =
    targetBounds.height > 1 && inkBounds.height > 1 ? targetBounds.height / inkBounds.height : 0;
  const widthRatio =
    targetBounds.width > 1 && inkBounds.width > 1 ? targetBounds.width / inkBounds.width : 0;
  let scale = targetBounds.height >= targetBounds.width ? heightRatio || widthRatio : widthRatio || heightRatio;
  if (heightRatio && widthRatio) {
    // Never let the secondary axis overshoot the frame by more than 35%.
    const cap = Math.min(heightRatio, widthRatio) * 1.35;
    scale = Math.min(scale, cap);
  }
  if (!scale || !Number.isFinite(scale) || scale <= 0) return ink;
  return ink.map((stroke) => ({
    points: stroke.points.map((point) => ({
      x: targetBounds.midX + (point.x - inkBounds.midX) * scale,
      y: targetBounds.midY + (point.y - inkBounds.midY) * scale,
    })),
  }));
}

function scoreGlyph(glyph, inkPoints, inkLength, difficulty) {
  if (!inkPoints.length) {
    return {
      templateID: glyph.templateID,
      displayName: glyph.displayName,
      score: 0,
      coverage: 0,
      precision: 0,
      matchedVariantID: glyph.variants[0].id,
      requiredMarksPresent: false,
    };
  }

  const tolerance = Math.max(5, Math.min(glyph.frame.width, glyph.frame.height) * difficulty.toleranceFraction);
  const results = glyph.variants.map((variantItem) => {
    const targetPoints = variantItem.strokes.flatMap((stroke) =>
      densify(stroke.points, stroke.isClosed, Math.max(2, tolerance * 0.22)),
    );
    const coverage = similarity(targetPoints, inkPoints, tolerance);
    const precision = similarity(inkPoints, targetPoints, tolerance);
    const geometricMean = Math.sqrt(Math.max(0, coverage * precision));
    const balancedScore = 0.65 * Math.min(coverage, precision) + 0.35 * geometricMean;
    const targetLength = variantItem.strokes.reduce(
      (total, stroke) => total + polylineLength(stroke.points, stroke.isClosed),
      0,
    );
    const lengthRatio = inkLength / Math.max(1, targetLength);
    const lengthPenalty = inkLengthPenalty(lengthRatio, difficulty.acceptedInkLengthRange);
    const requiredMarkCoverages = variantItem.strokes
      .filter((stroke) => stroke.kind === "dot")
      .map((stroke) =>
        similarity(
          densify(stroke.points, stroke.isClosed, Math.max(2, tolerance * 0.16)),
          inkPoints,
          tolerance * 0.72,
        ),
      );
    const requiredMarksPresent = requiredMarkCoverages.every((value) => value >= difficulty.minimumCoverage);
    const markCompleteness = requiredMarkCoverages.length
      ? Math.min(...requiredMarkCoverages)
      : 1;
    const structuralPenalty = requiredMarksPresent ? 1 : 0.35 + 0.45 * markCompleteness;
    return {
      templateID: glyph.templateID,
      displayName: glyph.displayName,
      score: balancedScore * lengthPenalty * structuralPenalty,
      coverage,
      precision,
      matchedVariantID: variantItem.id,
      requiredMarksPresent,
    };
  });

  return results.reduce((best, item) => (item.score > best.score ? item : best), results[0]);
}

export function makeTargetLayout(text, canvasSize) {
  const templates = String(text || "")
    .split("")
    .map((character) => drawingTemplateForCharacter(character))
    .filter(Boolean);
  if (!templates.length || canvasSize.width <= 1 || canvasSize.height <= 1) {
    return { text, canvasSize, glyphs: [] };
  }

  const horizontalInset = Math.max(24, canvasSize.width * 0.075);
  const verticalInset = Math.max(20, canvasSize.height * 0.065);
  const availableWidth = Math.max(1, canvasSize.width - horizontalInset * 2);
  const availableHeight = Math.max(1, canvasSize.height - verticalInset * 2);
  const desiredHeight = availableHeight;
  const desiredGap = desiredHeight * 0.075;
  const desiredWidths = templates.map((template) => desiredHeight * template.aspectRatio);
  const unscaledWidth = desiredWidths.reduce((a, b) => a + b, 0) + desiredGap * Math.max(0, templates.length - 1);
  const scale = Math.min(1, availableWidth / Math.max(1, unscaledWidth));
  const glyphHeight = desiredHeight * scale;
  const gap = desiredGap * scale;
  const glyphWidths = desiredWidths.map((width) => width * scale);
  const totalWidth = glyphWidths.reduce((a, b) => a + b, 0) + gap * Math.max(0, templates.length - 1);
  let cursorX = (canvasSize.width - totalWidth) / 2;
  const startY = (canvasSize.height - glyphHeight) / 2;

  const glyphs = templates.map((template, index) => {
    const glyphWidth = glyphWidths[index];
    const frame = {
      x: cursorX,
      y: startY,
      width: glyphWidth,
      height: glyphHeight,
      midX: cursorX + glyphWidth / 2,
      midY: startY + glyphHeight / 2,
    };
    cursorX += glyphWidth + gap;
    return {
      templateID: template.id,
      displayName: template.displayName,
      displayText: template.displayText,
      frame,
      variants: template.variants.map((variantItem) => ({
        id: variantItem.id,
        strokes: variantItem.strokes.map((stroke) => ({
          points: stroke.points.map((p) => ({
            x: frame.x + p.x * frame.width,
            y: frame.y + p.y * frame.height,
          })),
          kind: stroke.kind,
          isClosed: stroke.isClosed,
        })),
      })),
    };
  });

  return { text, canvasSize, glyphs };
}

export function evaluateInk(ink, layout, difficultyKey = "easy", placement = "anchored") {
  const difficulty = WRITING_DIFFICULTY[difficultyKey] || WRITING_DIFFICULTY.easy;
  if (!layout.glyphs.length) {
    return { score: 0, requiredScore: difficulty.acceptanceThreshold, accepted: false, glyphs: [] };
  }
  const evaluatedInk = placement === "freeform" ? fitToTarget(ink, layout) : ink;
  const denseInk = evaluatedInk.flatMap((stroke) => densify(stroke.points, false, 2.5));
  const assignedInk = assign(denseInk, layout.glyphs);
  const assignedInkLengths = assignLengths(evaluatedInk, layout.glyphs);
  const glyphResults = layout.glyphs.map((glyph, index) =>
    scoreGlyph(glyph, assignedInk[index], assignedInkLengths[index], difficulty),
  );
  const overallScore = glyphResults.reduce((sum, item) => sum + item.score, 0) / glyphResults.length;
  const perGlyphFloor = Math.max(0.46, difficulty.acceptanceThreshold - 0.12);
  const everyGlyphIsCredible = glyphResults.every(
    (glyph) =>
      glyph.score >= perGlyphFloor &&
      glyph.coverage >= difficulty.minimumCoverage &&
      glyph.precision >= difficulty.minimumPrecision &&
      glyph.requiredMarksPresent,
  );
  return {
    score: overallScore,
    requiredScore: difficulty.acceptanceThreshold,
    accepted: overallScore >= difficulty.acceptanceThreshold && everyGlyphIsCredible,
    glyphs: glyphResults,
  };
}

/**
 * @param placement "anchored" (tracing over a guide: ink must sit on the
 *   guide) or "freeform" (writing from memory: position/size are forgiven).
 */
export function evaluatePad(ink, layout, acceptanceThreshold = 0.7, difficultyKey = "easy", placement = "anchored") {
  if (!ink.some((stroke) => stroke.points.length >= 2)) return null;
  const band = placement === "freeform" && difficultyKey === "easy" ? "freeform" : difficultyKey;
  const result = evaluateInk(ink, layout, band, placement);
  if (acceptanceThreshold == null) return result;
  return {
    ...result,
    requiredScore: acceptanceThreshold,
    accepted: result.accepted && result.score >= acceptanceThreshold,
  };
}

export function evaluateWord(ink, layout, acceptanceThreshold = 0.7, placement = "anchored") {
  const result = evaluatePad(ink, layout, acceptanceThreshold, "easy", placement);
  if (!result) return null;
  const childFriendlyMatch =
    result.score >= acceptanceThreshold &&
    result.glyphs.every(
      (glyph) =>
        glyph.score >= 0.3 &&
        glyph.coverage >= 0.34 &&
        glyph.precision >= 0.22 &&
        glyph.requiredMarksPresent,
    );
  return { ...result, requiredScore: acceptanceThreshold, accepted: childFriendlyMatch };
}

export function bestLetter(ink, canvasSize, letters, acceptanceThreshold = 0.7, placement = "anchored") {
  const scored = letters
    .map((character) => {
      const layout = makeTargetLayout(character, canvasSize);
      const result = evaluateInk(ink, layout, placement === "freeform" ? "freeform" : "easy", placement);
      return { letter: character, result };
    })
    .filter((candidate) => candidate.result.accepted && candidate.result.score >= acceptanceThreshold);
  if (!scored.length) return null;
  return scored.reduce((best, item) => (item.result.score > best.result.score ? item : best));
}
